/* eslint-disable no-param-reassign */
import { TFunction, useSagaAnalytics } from "@einride/saga-core"
import {
  Checkbox,
  Loader,
  Option,
  Paragraph,
  SearchInput,
  SecondaryButton,
  Stack,
  Title3,
} from "@einride/ui"
import styled from "@emotion/styled"
import { AnimatePresence, AnimationProps, motion } from "framer-motion"
import { FilterAttributeTermsFragment, FilterTermFragment, QueryFilter } from "gen/graphql"
import { produce } from "immer"
import { EVENT_NAMES } from "lib/events"
import { useQueryParamWrapper } from "lib/hooks/useQueryParamWrapper"
import uniqBy from "lodash.uniqby"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { QueryParamConfig } from "use-query-params"
import { Container, Controls, StickyStack, StyledIcon } from "./Sidebar.styles"
import { getFilterDisplayName } from "./translate"

// getFilterAttributeTitle returns a translated string for a given filter attribute id.
export const getFilterAttributeTitle = (
  t: TFunction<"explore">,
  filterAttributeId: string,
): string => {
  switch (filterAttributeId) {
    case "space_id":
    case "space":
      return t("reportPage.space.key")
    case "origin_location_id":
    case "origin_location":
      return t("reportPage.location.key")
    case "origin_city":
      return t("reportPage.originCity.key")
    case "delivered_by_vehicle_display_name":
      return t("reportPage.deliveredByVehicle.key")
    case "destination_city":
      return t("reportPage.destinationCity.key")
    case "destination_country_code":
      return t("reportPage.destinationCountryCode.key")
    case "origin_country_code":
      return t("reportPage.originCountryCode.key")
    case "owner_organization_name":
      return t("reportPage.ownerOrganizationName.key")
    case "status":
      return t("reportPage.status.key")
    case "destination_location_id":
    case "destination_location":
      return t("reportPage.destinationLocation.key")
    case "lane_id":
    case "lane":
      return t("reportPage.lane.key")
    case "vehicle_energy_source":
      return t("reportPage.vehicleEnergySource.key")
    case "vehicle_drive_type":
      return t("reportPage.vehicleDriveType.key")
    default:
      return filterAttributeId
  }
}

type ContentStep = "initial" | "filter"

const animation: AnimationProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.05 },
}

interface StepParam {
  step: ContentStep
  filterAttributeId: string | undefined
}

export const StepParam: QueryParamConfig<StepParam> = {
  encode: (value) => {
    return JSON.stringify(value)
  },
  decode: (value) => {
    if (value && typeof value === "string") {
      return JSON.parse(value)
    }
    return {
      step: "initial",
      filterAttributeId: undefined,
    }
  },
  default: {
    step: "initial",
    filterAttributeId: undefined,
  },
}

type SheetState = {
  header: React.JSX.Element
  content: React.JSX.Element
}

export type Filter = {
  filterTerms: FilterTerm[]
} & FilterAttributeTermsFragment

type FilterTerm = {
  isActive?: boolean
} & FilterTermFragment

interface FilterViewProps {
  onClose: () => void
  visible: boolean
  onApply: (filters: QueryFilter[]) => void
  availableFilterAttributeIds: string[]
  filterData: FilterAttributeTermsFragment[]
  isFilterDataLoading: boolean
  filters: Filter[]
  setFilters: Dispatch<SetStateAction<Filter[]>>
}

export const FilterView = ({
  onApply,
  onClose,
  visible,
  isFilterDataLoading,
  availableFilterAttributeIds,
  filters,
  setFilters,
}: FilterViewProps): React.JSX.Element => {
  const { t } = useTranslation()
  const { trackEvent } = useSagaAnalytics()
  const [filterViewStep, setFilterViewStep] = useQueryParamWrapper("filterViewStep", StepParam)
  const [visibleFilterTerms, setVisibleFilterTerms] = useState<FilterTerm[]>([])
  const [visibleFilters, setVisibleFilters] = useState(availableFilterAttributeIds)
  const [filterTermSearchTerm, setFilterTermSearchTerm] = useState("")
  const [filterSearchTerm, setFilterSearchTerm] = useState("")

  const isAnyFilterSelected = !!findFilter(
    filterViewStep.filterAttributeId,
    filters,
  )?.filterTerms.filter(activeFilterTerms).length

  const moveToInitialStep = (): void => {
    setFilterViewStep({
      step: "initial",
      filterAttributeId: undefined,
    })
  }

  const moveToFilterStep = (value: string): void => {
    trackEvent({
      key: EVENT_NAMES.FILTER_KEY_CLICKED,
      properties: {
        report_type: "insight report",
        has_filters_applied: isAnyFilterSelected,
        filter_name: value,
      },
    })
    setFilterViewStep({
      step: "filter",
      filterAttributeId: value,
    })
    setFilterSearchTerm("")
  }

  const applyFilter = (filter: Filter): void => {
    const index = filters.findIndex((f) => f.filterAttributeId === filter.filterAttributeId)
    if (index >= 0) {
      const updatedFilters = produce(filters, (draft) => {
        draft.splice(index, 1, filter)
      })
      onApply(
        updatedFilters.map(filterInactiveFilters).filter(filterEmptyFilters).map(toQueryFilter),
      )
      setFilters(updatedFilters)
    }
  }

  const toggleAllFilters = (value: boolean): void => {
    if (!filterViewStep.filterAttributeId) {
      return
    }
    const filter = findFilter(filterViewStep.filterAttributeId, filters)
    if (filter) {
      const updatedFilter = toggleFilterTerms(value, filter)
      applyFilter(updatedFilter)
    }
  }

  const onFilterTermClick = (
    filterAttributeId: string | undefined,
    filterTerm: FilterTermFragment,
    checked: boolean,
  ): void => {
    trackEvent({
      key: EVENT_NAMES.FILTER_VALUE_CLICKED,
      properties: {
        report_type: "insight report",
        has_filters_applied: isAnyFilterSelected,
        filter_key: filterAttributeId,
      },
    })
    const filter = findFilter(filterAttributeId, filters)
    if (filter) {
      const updatedFilter = toggleFilterTerm(checked, filter, filterTerm.displayName)
      applyFilter(updatedFilter)
    }
  }

  // set initial filteredFilterTerms when selecting a new active filter attribute id and searchTerm is not set
  useEffect(() => {
    if (!filterTermSearchTerm || !filterViewStep.filterAttributeId) {
      setFilterTermSearchTerm("")
      setVisibleFilterTerms(
        uniqBy(
          findFilter(filterViewStep.filterAttributeId, filters)?.filterTerms ?? [],
          (filterTerm) => filterTerm.displayName,
        ),
      )
    }
    if (!filterSearchTerm) {
      setFilterSearchTerm("")
      setVisibleFilters(availableFilterAttributeIds)
    }
  }, [
    availableFilterAttributeIds,
    filterSearchTerm,
    filterTermSearchTerm,
    filterViewStep.filterAttributeId,
    filters,
  ])

  const handleSearchTermChange = (searchTerm: string): void => {
    setFilterSearchTerm(searchTerm)
    if (searchTerm === "") {
      setVisibleFilters(availableFilterAttributeIds)
    } else {
      setVisibleFilters(
        availableFilterAttributeIds.filter((attributeId) =>
          getFilterAttributeTitle(t, attributeId).includes(searchTerm),
        ),
      )
    }
  }

  const onSearchFilterTermChange = (searchTerm: string): void => {
    setFilterTermSearchTerm(searchTerm)
    const terms = findFilter(filterViewStep.filterAttributeId, filters)?.filterTerms ?? []
    // show all terms if search term is empty
    if (searchTerm === "") {
      setVisibleFilterTerms(terms)
    } else {
      const filteredTerms = terms?.filter((term) =>
        term.displayName.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      setVisibleFilterTerms(filteredTerms)
    }
  }

  // we want to show two steps in the same sheets instead of
  // close and reopen a sheet, to do that we need to
  // map each step to its spec to specify how it looks and work
  const stepStateMachine: Record<ContentStep, SheetState> = {
    initial: {
      header: (
        <StickyStack>
          <Controls>
            <StyledIcon
              data-testid="back-filter-view"
              aria-label="back filter view"
              onClick={() => {
                setFilterViewStep({
                  step: "initial",
                  filterAttributeId: undefined,
                })
                onClose()
              }}
              name="chevronLeft"
              color="secondary"
            />
            <FilterStepTitle>{t("reportPage.filtersTitle")}</FilterStepTitle>
          </Controls>
          <SearchInput
            aria-label={t("reportPage.filtersSearchInputPlaceholder")}
            placeholder={t("reportPage.filtersSearchInputPlaceholder")}
            value={filterSearchTerm}
            onInputChange={handleSearchTermChange}
          />
        </StickyStack>
      ),
      content: (
        <motion.div
          key="initial"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...animation}
        >
          <Stack gap="sm">
            {visibleFilters.map((filter) => {
              return (
                <Option
                  key={filter}
                  onClick={() => moveToFilterStep(filter)}
                  data-testid={`filter-view-${filter}`}
                >
                  {getFilterAttributeTitle(t, filter)}
                </Option>
              )
            })}
          </Stack>
        </motion.div>
      ),
    },
    filter: {
      header: (
        <StickyStack>
          <Controls>
            <StyledIcon
              data-testid="back-filter-view"
              aria-label="back filter view"
              onClick={moveToInitialStep}
              name="chevronLeft"
              color="secondary"
            />
            <FilterStepTitle>
              {getFilterAttributeTitle(t, filterViewStep.filterAttributeId ?? "")}
            </FilterStepTitle>
            <CloseButton
              data-testid="close-filter-view"
              onClick={() => {
                setFilterViewStep({
                  step: "initial",
                  filterAttributeId: undefined,
                })
                onClose()
              }}
            >
              {t("common.done")}
            </CloseButton>
          </Controls>
          <SearchInput
            aria-label={t("reportPage.filtersSearchInputPlaceholder")}
            data-testid="filter-view-search-input"
            placeholder={t("reportPage.filtersSearchInputPlaceholder")}
            value={filterTermSearchTerm}
            onInputChange={onSearchFilterTermChange}
          />
        </StickyStack>
      ),
      content: (
        <motion.div
          key="filter"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...animation}
          transition={{ ...animation.transition, delay: 0.1 }}
        >
          <FullHeightStack gap={0}>
            {isFilterDataLoading && <Loader />}
            {!findFilter(filterViewStep.filterAttributeId, filters)?.filterTerms.length &&
              !isFilterDataLoading && <Paragraph>{t("reportPage.noAvailableFilters")}</Paragraph>}
            {!!findFilter(filterViewStep.filterAttributeId, filters)?.filterTerms.length && (
              <Checkbox
                data-testid="filter-view-checkbox"
                onCheckedChange={toggleAllFilters}
                checked={isAnyFilterSelected}
              >
                {isAnyFilterSelected
                  ? t("reportPage.deselectAllFilters")
                  : t("reportPage.selectAllFilters")}
              </Checkbox>
            )}
            {visibleFilterTerms.map((term) => (
              <Ellipsis key={term.displayName}>
                <Checkbox
                  data-testid="filter-view-checkbox"
                  onCheckedChange={(checked) =>
                    onFilterTermClick(filterViewStep.filterAttributeId, term, checked)
                  }
                  checked={isFilterTermChecked(filterViewStep.filterAttributeId, term, filters)}
                >
                  {getFilterDisplayName(term, t)}
                </Checkbox>
              </Ellipsis>
            ))}
          </FullHeightStack>
        </motion.div>
      ),
    },
  }

  const spec = stepStateMachine[filterViewStep.step]

  return (
    <Container
      data-testid="filter-view"
      initial={false}
      animate={visible ? "open" : "closed"}
      visible={visible}
      transition={{
        duration: 0.2,
      }}
      variants={{
        open: { opacity: 1, x: 0 },
        closed: { opacity: 0, x: "100%" },
      }}
    >
      {spec.header}
      <AnimatePresence>{spec.content}</AnimatePresence>
    </Container>
  )
}

const Ellipsis = styled.div`
  label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const CloseButton = styled(SecondaryButton)`
  position: absolute;
  right: 0;
`

const FilterStepTitle = styled(Title3)`
  margin: 0;
`

const FullHeightStack = styled(Stack)`
  height: 100%;
  justify-content: flex-start;
`

const isFilterTermChecked = (
  filterAttributeId: string | undefined,
  term: FilterTermFragment,
  filters: Filter[],
): boolean => {
  if (!filterAttributeId) {
    return false
  }
  const filter = findFilter(filterAttributeId, filters)
  const foundTerm = filter?.filterTerms.find((o) => o.id === term.id)
  return foundTerm ? foundTerm.isActive ?? false : false
}

export const findFilter = (
  filterAttributeId: string | undefined,
  filters: Filter[],
): Filter | undefined => filters.find((filter) => filter.filterAttributeId === filterAttributeId)

export const toggleFilterTerms = (value: boolean, filter: Filter): Filter => ({
  ...filter,
  filterTerms: filter.filterTerms.map((term) => ({
    ...term,
    isActive: value,
  })),
})

export const toggleFilterTerm = (
  value: boolean,
  filter: Filter,
  filterTermName: string,
): Filter => {
  return produce(filter, (draft) => {
    draft.filterTerms.forEach((filterTerm) => {
      if (filterTerm.displayName === filterTermName) {
        filterTerm.isActive = value
      }
    })
  })
}

const activeFilterTerms = (filterTerm: FilterTerm): boolean => {
  return filterTerm.isActive ?? false
}

export const filterInactiveFilters = (filter: Filter): Filter => {
  return {
    ...filter,
    filterTerms: filter.filterTerms.filter((term) => term.isActive),
  }
}

export const filterEmptyFilters = (filter: Filter): boolean => {
  return !!filter.filterTerms.length
}

export const toQueryFilter = (filter: Filter): QueryFilter => {
  return {
    filterAttributeId: filter.filterAttributeId,
    filterTermIds: filter.filterTerms.map((term) => term.id),
  }
}

export const getActiveFilterTermDisplayNames = (
  filterAttributeId: string,
  filters: Filter[],
): FilterTerm[] => {
  const filter = findFilter(filterAttributeId, filters)
  if (filter) {
    return filter.filterTerms.filter(activeFilterTerms)
  }
  return []
}
